import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Traps keyboard focus inside `containerRef` when `active` is true.
 * Restores focus to the previously-focused element on deactivation.
 *
 * Usage:
 *   const dialogRef = useRef(null);
 *   useFocusTrap(dialogRef, isOpen);
 *   return <div ref={dialogRef} role="dialog" aria-modal="true"> ... </div>
 */
export function useFocusTrap(containerRef, active) {
  const previousFocus = useRef(null);

  useEffect(() => {
    if (!active) return;

    previousFocus.current = document.activeElement;

    // Focus the first focusable element inside the container.
    const first = containerRef.current?.querySelector(FOCUSABLE);
    first?.focus();

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;
      const els = Array.from(containerRef.current?.querySelectorAll(FOCUSABLE) ?? []);
      if (!els.length) return;

      const first = els[0];
      const last = els[els.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus.current?.focus();
    };
  }, [active, containerRef]);
}
